import classNames from "classnames";
import styles from "./SideBar.module.css";
import { SectionAccordion, useDeviceClassification } from "utils";
import { useSubPageContent } from "utils";
import React, { FC } from "react";
import he from "he";

export interface SideBarProps {
  className?: string;
}

type TSidebarWidgetProps = {
  title: string;
  content: { Title: string, Url: string }[],
  jumplinks?: boolean;
}

const SidebarWidget: FC<TSidebarWidgetProps> = ({ title, content, jumplinks }) => {

  /** 
  1. Fn for iterating over h2 headings containing id's and title.
  2. Fn creates a list of anchor-tags with href attributes set to corresponding id's.
  */
  const createJumplinks = () => {
    const h2List = document.getElementsByTagName('h2');
    const nodes = Array.from(h2List).map((v, i) => {
      const txt = v.title || v.innerText
      if (v.id) {
        return <li key={v.title + i} className="jump-link"><a className="underline-green button-2022 jump-link" href={`#${v.id}`}>{txt}</a></li>
      };

      return null
    });

    return nodes.filter((n) => Boolean(n))
  };

  const jumpLinksArray = jumplinks && createJumplinks();

  return (
      <div key={`SidebarWidget-${title}`}>
        <span style={{ paddingBottom: "1px" }} dangerouslySetInnerHTML={{ __html: title }}></span>

        {jumplinks && <ul>
          <div>{jumpLinksArray}</div>
        </ul>}

        {typeof content === "string" && (
          title === "Features" ?
            <ul>
              <div dangerouslySetInnerHTML={{ __html: content }}></div>
            </ul>
            :
            <p dangerouslySetInnerHTML={{ __html: content }}></p>
        )}

        {typeof content === "object" && (
          <div className="linklist">
              {content?.length && content.map(({ Title, Url }, i) => {
                const addComma = i + 1 < content.length
                if (!Title || !Url) return null;
                return (
                  <a key={`SidebarWidget-anchor-${i}-${Title}`} href={Url}>{he.decode(Title)}{addComma && ', '}</a>
                );
              })}
            </div>
        )}
      </div>
  );
};

const SidebarContent = () => {
  const {
    QuickFactsLocationTitle,
    QuickFactsLocationContent,
    QuickFactsClientTitle,
    QuickFactsClientContent,
    QuickFactsProjectPartnersTitle,
    QuickFactsProjectPartnersContent,
    QuickFactsAureconRoleTitle,
    QuickFactsAureconRoleContent,
    QuickFactsAureconExpertiseTitle,
    QuickFactsAureconExpertiseContent,
    QuickFactsStatusTitle,
    QuickFactsStatusContent,
    QuickFactsFeaturesTitle,
    QuickFactsFeaturesContent,
  } = useSubPageContent();

  return (
    <>
      {QuickFactsLocationTitle && Boolean(QuickFactsLocationContent) && (
        <SidebarWidget
          key={QuickFactsLocationTitle}
          title={QuickFactsLocationTitle}
          content={QuickFactsLocationContent}
        />
      )}

      {QuickFactsClientTitle && Boolean(QuickFactsClientContent) && (
        <SidebarWidget
          key={QuickFactsClientTitle}
          title={QuickFactsClientTitle}
          content={QuickFactsClientContent}
        />
      )}

      {QuickFactsProjectPartnersTitle && Boolean(QuickFactsProjectPartnersContent) && (
        <SidebarWidget
          key={QuickFactsProjectPartnersTitle}
          title={QuickFactsProjectPartnersTitle}
          content={QuickFactsProjectPartnersContent}
        />
      )}

      {QuickFactsAureconRoleTitle && Boolean(QuickFactsAureconRoleContent) && (
        <SidebarWidget
          key={QuickFactsAureconRoleTitle}
          title={QuickFactsAureconRoleTitle}
          content={QuickFactsAureconRoleContent}
        />
      )}

      {QuickFactsAureconExpertiseTitle && Boolean(QuickFactsAureconExpertiseContent.length) && (
        <SidebarWidget
          key={QuickFactsAureconExpertiseTitle}
          title={QuickFactsAureconExpertiseTitle}
          content={QuickFactsAureconExpertiseContent}
        />
      )}

      {QuickFactsStatusTitle && Boolean(QuickFactsStatusContent) && (
        <SidebarWidget
          key={QuickFactsStatusTitle}
          title={QuickFactsStatusTitle}
          content={QuickFactsStatusContent}
        />
      )}

      {QuickFactsFeaturesTitle && (
        <SidebarWidget
          jumplinks={!Boolean(QuickFactsFeaturesContent)}
          key={QuickFactsFeaturesTitle}
          title={QuickFactsFeaturesTitle}
          content={QuickFactsFeaturesContent}
        />
      )}
    </>
  );
};

export const SideBar = ({ className = "", ...props }: SideBarProps) => {
  const { isTablet, isDesktop } = useDeviceClassification();

  return (
    <>
      <div
        className={classNames(styles.sidebar, styles.sidebarmain, className, isDesktop ? 'col-3' : 'col-12')}
        {...props}
      >
        {!isTablet && <SidebarContent />}

        {isTablet && (
          <SectionAccordion
            moreButtonText="View quick facts"
            lessButtonText="Hide quick facts"
          >
            <div className={styles.sidebar}>
              <SidebarContent />
            </div>
          </SectionAccordion>
        )}
      </div>
    </>

  );
};
